import React, { FC, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  ThemeProvider,
  createTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './BaseModal.module.scss';

const theme = createTheme({
  typography: {
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
  },
  palette: {
    primary: {
      main: '#4D4D4D',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.primary': {
            backgroundColor: 'var(--brand-primary)',
            '&.Mui-disabled': {
              backgroundColor: 'var(--disabled-button-bg-dark)',
              color: 'white',
            },
          },
        },
      },
    },
  },
});

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: string;
  children: ReactNode;
  actions?: ReactNode;
  sx?: any;
}

const BaseModal: FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  titleIcon,
  children,
  actions,
  sx
}) => {
  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        className={styles.baseModalWrapper}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          },
          ...sx
        }}
      >
        <DialogTitle>
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={0}>
              <IconButton 
                onClick={onClose} 
                size="medium"
                sx={{ 
                  color: '#333333 !important',
                  padding: '0px',
                  '& .MuiSvgIcon-root': {
                    color: '#333333',
                    fontSize: '28px',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5} mt={0}>
                {titleIcon && <img src={titleIcon} width={80} alt="" />}
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 400,
                    color: '#253166'
                  }}
                >
                  {title}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {children}
        </DialogContent>

        {actions && (
          <DialogActions>
            <Box display="flex" justifyContent="center" width="100%">
              {actions}
            </Box>
          </DialogActions>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default BaseModal;